/** @file bone.split: one bone becomes two at a cut; its parts there become meshes weighted across the cut. */
import { z } from 'zod';
import type { Draft } from 'immer';
import { slugifyName } from '@pose2d/format/slugifyName';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { applyNested } from '../autorig/applyNested';
import { assertFreeName } from '../_shared/assertFreeName';
import { assertNotSocketBone } from '../_shared/assertNotSocketBone';
import { splitMeshes } from './splitMeshes';

/** Refuses the root, a generated socket bone, a bone without length and a bone an IK constraint uses. */
function assertSplittable(doc: Doc, id: Id): void {
  const bone = doc.bones[id];
  if (!bone || bone.parent === null) throwError('VALIDATION', undefined, { path: 'target', hint: 'the root bone cannot be split; split one of its children' });
  assertNotSocketBone(doc, id);
  if (!(bone.length > 0)) throwError('VALIDATION', undefined, { path: 'target', hint: `bone ${bone.name} has no length; set one first (bone.setLength)` });
  for (const c of Object.values(doc.constraints)) {
    if (c.bones.includes(id) || c.target === id) throwError('IK_INVALID', { ik: c.name, reason: 'a bone an IK constraint uses cannot be split' }, { path: 'target', hint: `delete IK ${c.name} first, split, then create it on the new chain` });
  }
}

/** `<name>-2` with the side suffix kept last (`arm-L` → `arm-2-L`), made unique among the bones. */
function defaultName(doc: Doc, name: string): string {
  const side = /-[LR]$/.test(name) ? name.slice(-2) : '';
  const base = `${side ? name.slice(0, -2) : name}-2${side}`;
  return slugifyName(base, new Set(Object.values(doc.bones).map((b) => b.name)), 0).name;
}

/** Moves the bone's child bones onto the new bone, keeping their world setup; socket bones stay (INFO). */
function moveChildren(draft: Draft<Doc>, ctx: CommandContext, id: Id, added: Id, cut: number): void {
  let sibling = 0;
  for (const child of ctx.index.children.get(id) ?? []) {
    const b = draft.bones[child];
    if (!b || child === added) continue;
    if (b.kind !== undefined) {
      ctx.warnings.push({ code: 'INFO', message: `socket bone ${b.name} stays on ${draft.bones[id]?.name ?? id}; move the socket with socket.setPoints if the item belongs past the cut`, path: 'target' });
      continue;
    }
    b.parent = added;
    b.siblingIndex = sibling++;
    b.setup.x -= cut;
  }
}

/** bone.split (X1.7, AX1-08): see the description. */
export const command = defineCommand({
  name: 'bone.split',
  description: 'Split a bone in two at `at` (0..1 of its length, default 0.5): it keeps the part before the cut and a new child bone `name` (default <bone>-2, side suffix last) takes the rest and its child bones, whose world setup is kept (socket bones stay). With mesh (default true) each unmounted region its slots show becomes a grid mesh, and every weight on the bone (rigid meshes only its slots show, skinned meshes anywhere) is shared with the new bone across the cut, so the part bends there. Refused for the root, socket bones, a bone with no length and a bone an IK constraint uses.',
  input: z.object({ target: z.string().min(1), at: z.number().gt(0).lt(1).exactOptional(), name: z.string().min(1).exactOptional(), mesh: z.boolean().exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const id = ctx.resolve('bone', input.target);
    assertSplittable(draft, id);
    const bone = draft.bones[id];
    if (!bone) return;
    const name = input.name ?? defaultName(draft, bone.name);
    assertFreeName(ctx, 'bone', name);
    const length = bone.length;
    const cut = length * (input.at ?? 0.5);
    const made = applyNested(draft, { name: 'bone.create', input: { name, parent: id, length: length - cut, setup: { x: cut } } }, ctx, 'target');
    const added = made[name] ?? '';
    Object.assign(ctx.created, made);
    moveChildren(draft, ctx, id, added, cut);
    bone.length = cut;
    if (input.mesh !== false) splitMeshes(draft, ctx, { bone: id, added, cut, length });
    draft.meta.updatedAt = ctx.now;
  },
});
